"use client";

import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    GraduationCap,
    Users,
    Building2,
    BookOpen,
    Wifi,
    Stethoscope,
    FlaskConical,
    UsersRound,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { facilitiesData } from "@/utils/infradata";
import Intro from "@/ui/Intro";
import MediaGallery from "./MediaGallery";

const iconMap = {
    GraduationCap,
    Users,
    Building2,
    BookOpen,
    Wifi,
    Stethoscope,
    FlaskConical,
    UsersRound,
};

export default function Mediasection() {
    const [activeTab, setActiveTab] = useState(facilitiesData[0].id);
    const tabContainerRef = useRef(null);

    const activeCategory =
        facilitiesData.find((cat) => cat.id === activeTab) || facilitiesData[0];

    const scrollTabs = (direction) => {
        if (tabContainerRef.current) {
            const scrollAmount = 300;
            const newScrollLeft =
                tabContainerRef.current.scrollLeft +
                (direction === "left" ? -scrollAmount : scrollAmount);
            tabContainerRef.current.scrollTo({
                left: newScrollLeft,
                behavior: "smooth",
            });
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <section>
                <Intro
                    title="State-of-the-Art Campus Facilities"
                    description="Experience smart classrooms, advanced labs, modern amenities and vibrant learning spaces across MSU"
                />
            </section>

            {/* Sticky tabs bar */}
            <div className="sticky md:top-20 top-0 z-40 bg-white ">
                <div className="">
                    <div className="flex items-center gap-3">
                        <div
                            ref={tabContainerRef}
                            className="flex gap-2 overflow-x-auto scrollbar-hide py-4 flex-1"
                        >
                            {facilitiesData.map((category, index) => {
                                const isActive = activeTab === category.id;

                                return (
                                    <motion.button
                                        key={category.id}
                                        onClick={() => setActiveTab(category.id)}
                                        className={`relative px-6 py-2.5 cursor-pointer border-[#00000033] whitespace-nowrap transition-all ${isActive
                                            ? "bg-[#FFA201] text-white shadow-md"
                                            : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                                            }`}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                        whileHover={{ scale: isActive ? 1 : 1.05 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <span
                                            className="text-sm"
                                            style={{ fontFamily: "saans-regular" }}
                                        >
                                            {category.name}
                                        </span>
                                    </motion.button>
                                );
                            })}
                        </div>

                        <div className="hidden md:flex items-center gap-1">
                            <button
                                onClick={() => scrollTabs("left")}
                                className="p-2 hover:bg-neutral-100 text-neutral-600 transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => scrollTabs("right")}
                                className="p-2 hover:bg-neutral-100 text-neutral-600 transition-colors"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content below sticky tabs */}
            <section className="py-2 pb-8">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <MediaGallery media={activeCategory.media} />
                    </motion.div>
                </AnimatePresence>
            </section>
        </div>
    );
}
